/* eslint-disable no-useless-escape */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { FiPlus } from 'react-icons/fi';
import {
  IoIosArrowBack,
  IoIosTrash,
  IoMdInformationCircle,
} from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { MoonLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { ButtonBlack, Input, Select, Textarea } from '~/components';
import { useMuscleGroup } from '~/hooks/muscleGroup';
import { usePrescription } from '~/hooks/prescription';
import { useSequency } from '~/hooks/sequency';
import { useStudent } from '~/hooks/student';
import { SequenciesResponse, Sequency } from '~/models/Sequencies';
import api from '~/services/api';
import { formatProfileCode } from '~/utils/formatProfileCode';
import getValidationErrors from '~/utils/getValidationErrors';

import { IProgram } from '../../../interface';
import Modal from './Modal';
import SequenceCard from './SequenceCard';
import {
  TrainingStoresRequestData,
  DataFormProps,
  ExEditorsResponse,
  SelectProps,
  RouteParams,
  InitialFormProps,
  DivisionData,
  DivisionDataResults,
} from './types';

import {
  ModalContainer,
  PageHeader,
  HeaderWrapper,
  FirstScreenHeaderContainer,
  FinishTrainingButton,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  InfoButton,
  Info,
  ContentContainer,
  InfoContent,
  RowContent,
  Section,
  NewButtonContainer,
  NewButton,
  AlertContainer,
  NewButtonLabel,
  Message,
  ModalSequenceContainer,
  FieldSet,
  FieldSetWrapper,
  FieldTitle,
  CardContainer,
  RowContainer,
  InputLabel,
  InputSet,
  RadioInput,
  SecondPageHeader,
  SecondaryHeaderLine,
  SecondaryHeaderBackButton,
  SecondaryHeaderContinueButton,
  SecondaryHeaderInfo,
  DeleteModalContainer,
} from './styles';

export function PrescriptionScreen() {
  const { goBack, location } = useHistory();
  const { userId: studentId, billingId } = useParams<RouteParams>();
  const { studentData, updateStudent } = useStudent();
  const { sequencyLabel, selectedSequency, updateSequencies } = useSequency();
  const { prescription } = usePrescription();
  const { groups } = useMuscleGroup();

  const { division, programId } = useState(
    location.state as {
      division: DivisionDataResults;
      programId: number;
    },
  )[0];

  const [program, setProgram] = useState({} as IProgram);
  const [loading, setLoading] = useState(true);
  const [totalNumberOfSeries, setTotalNumberOfSeries] = useState(0);
  const [panelOfControlId, setPanelOfControlId] = useState<number>();
  const [sequencies, setSequencies] = useState<Sequency[]>([]);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDeleteSequencyModal, setShowDeleteSequencyModal] = useState(false);
  const [screenSwitch, setScreenSwitch] = useState(true); // true = firstScreen false = secondScreen
  const [showSequencesModal, setShowSequencesModal] = useState(false);
  const [allSequenciesIsFinished, setAllSequenciesIsFinished] = useState(false);

  // Check sequencies finalized
  useEffect(() => {
    const b =
      sequencies.length &&
      sequencies
        .map((sequency) =>
          sequency.prescriptions.map(
            (presc) =>
              Object.values(presc).filter((value) => value === '').length <= 7,
          ),
        )
        .flat()
        .filter((value) => value === false);

    if (b === 0) {
      setAllSequenciesIsFinished(false);
    } else if (b && b.length > 0) {
      setAllSequenciesIsFinished(false);
    } else {
      setAllSequenciesIsFinished(true);
    }
  }, [sequencies]);

  // pega id da sequencia de treinamento
  useEffect(() => {
    async function fetchData() {
      const pocId = program?.controlPanel?.id;

      pocId && setPanelOfControlId(pocId);
    }

    fetchData();
  }, [program]);

  const loadSequencies = useCallback(async () => {
    try {
      const { data } = await api.get<SequenciesResponse>(
        `training-programs/${division.id}/divisions/${division.id}/sequencies`,
      );

      setSequencies(data.results);

      if (data.results.length > 0) {
        updateSequencies(division.id as number);
      }

      const numberOfSeries = data?.results
        .map((seq) => seq?.prescriptions.map((presc) => presc.number_of_series))
        .flat()
        .reduce((prev, next) => Number(prev) + Number(next));

      setTotalNumberOfSeries(numberOfSeries || 0);
      // handleDisableRecoveryTimeInput(parsedIntValue);
      // formRef.current?.setFieldValue('number_of_series', value || 0);
    } catch (error) {
      console.log('error:', error);
    }

    setLoading(false);
  }, [division, programId]);

  useEffect(() => {
    loadSequencies();
    updateStudent(studentId);
  }, [updateStudent, studentId, loadSequencies]);

  const handleGetDivisions = useCallback(async () => {
    const response = await api.get<DivisionData>(
      `training-programs/${programId}/divisions`,
    );

    setProgram(response.data.header);
  }, [programId]);

  useEffect(() => {
    handleGetDivisions();
  }, [handleGetDivisions]);

  function InfoModal() {
    const genderVariations: { [key: string]: string } = {
      male: 'MASCULINO',
      female: 'FEMININO',
      others: 'OUTRO',
      other: 'OUTRO',
      '': 'Não informado',
      null: 'Não informado',
    };

    return (
      <Modal
        isOpen={showInfoModal}
        setOpen={setShowInfoModal}
        maxWidthStyled={600}
        maxHeightStyled={600}
      >
        <ModalContainer>
          <h1>Informações do Treino</h1>

          <ContentContainer>
            <RowContent>
              <strong>Fatura: </strong>
              <p>{billingId}</p>
            </RowContent>

            <RowContent>
              <strong>Programa: </strong>
              {programId}
            </RowContent>

            <RowContent>
              <strong>Nome do aluno: </strong>
              {studentData.name}
            </RowContent>

            <RowContent>
              <strong>Sexo: </strong>
              {genderVariations[studentData.student.genre]}
            </RowContent>

            <RowContent>
              <strong>Nível do aluno: </strong>
              {program?.datasheet?.student_level}
            </RowContent>

            <RowContent>
              <strong>Avaliação: </strong>
              {program?.datasheet?.classification}
            </RowContent>

            <RowContent>
              <strong>Objetivo: </strong>
              {program?.datasheet?.objective}
            </RowContent>

            <RowContent>
              <strong>Frequência: </strong>
              {program?.datasheet?.frequency}
            </RowContent>

            <RowContent>
              <strong>Tempo disponível: </strong>
              {program?.datasheet?.available_time}
            </RowContent>

            <RowContent>
              <strong>Divisão de treino: </strong>
              {program?.training_division}
            </RowContent>
          </ContentContainer>

          <div className="profile-code">
            <h2>Profile Code:</h2>
            <h3>{formatProfileCode(program?.datasheet?.profile_code)}</h3>
          </div>

          <div className="sheet-model">
            <h2>Modelo de ficha:</h2>
            <h3>{program?.data_model}</h3>
          </div>
        </ModalContainer>
      </Modal>
    );
  }

  const handleDeleteSequencies = async () => {
    try {
      await api.delete(
        `training-programs/1/divisions/1/sequencies/${selectedSequency.id}`,
      );

      toast.success('Sequência exluída com sucesso!');
      goBack();
    } catch (error: any) {
      console.log(error.message);
      toast.error('Erro ao exluír sequência!');
    }
  };

  function DeleteSequencyModal() {
    return (
      <Modal
        isOpen={showDeleteSequencyModal}
        setOpen={setShowDeleteSequencyModal}
        maxWidthStyled={500}
        maxHeightStyled={250}
      >
        <DeleteModalContainer>
          <p>VOCÊ DESEJA DELETAR ESTA SEQUÊNCIA?</p>

          <div>
            <button type="button" onClick={() => handleDeleteSequencies()}>
              Sim
            </button>

            <button
              type="button"
              onClick={() => setShowDeleteSequencyModal(false)}
            >
              Não
            </button>
          </div>
        </DeleteModalContainer>
      </Modal>
    );
  }

  function SequenceModal() {
    const [radioStatusOptions, setRadioStatusOptions] = useState('1');
    const [loadingSM, setLoadingSM] = useState(false);

    const radioStatusOptionsList = useMemo(
      () => ['1', '1 bi-set', '1 tri-set'],
      [],
    );

    const RadioStatusOptions = useMemo(
      () =>
        radioStatusOptionsList.map((item) => (
          <RadioInput
            key={item}
            isSelected={item === radioStatusOptions}
            onClick={() => setRadioStatusOptions(item)}
          >
            <div className="radio">
              <div className="filled" />
            </div>

            <p>{item}</p>
          </RadioInput>
        )),
      [radioStatusOptionsList, radioStatusOptions],
    );

    const handleSubmit = useCallback(async () => {
      setLoadingSM(true);

      const data = {
        description: radioStatusOptions,
        number: radioStatusOptionsList.indexOf(radioStatusOptions) + 1,
      };

      try {
        await api.post(
          `training-programs/${division.id}/divisions/${division.id}/sequencies`,
          data,
        );

        toast.success(`A sequência foi gerada`);

        await loadSequencies();

        setShowSequencesModal(false);
      } catch (err) {
        console.log(err);

        toast.error(`Tente novamente`);
      }

      setLoadingSM(false);
    }, [radioStatusOptions, radioStatusOptionsList]);

    return (
      <Modal
        isOpen={showSequencesModal}
        setOpen={setShowSequencesModal}
        maxWidthStyled={621}
        maxHeightStyled={361}
      >
        <ModalSequenceContainer>
          <FieldSet>
            <FieldSetWrapper>
              <FieldTitle>Tipo de sequência:</FieldTitle>
              {RadioStatusOptions}
            </FieldSetWrapper>
          </FieldSet>

          <ButtonBlack disabled={loadingSM} onClick={handleSubmit}>
            Selecionar
          </ButtonBlack>
        </ModalSequenceContainer>
      </Modal>
    );
  }

  const loadSequenciesComponent = useMemo(
    () => (
      <Section>
        {sequencies.length ? (
          <CardContainer>
            {sequencies.map((sequency, index) => {
              return sequency.prescriptions.map(
                (prescription_, prescriptionIndex) => {
                  const isChecked: boolean =
                    (prescription_?.exercise &&
                      prescription_.exercise.length > 0) ||
                    false;

                  return (
                    <SequenceCard
                      key={prescription_.id}
                      sequencyNumber={index + 1}
                      prescriptionNumber={prescriptionIndex}
                      switchScreen={setScreenSwitch}
                      sequency={sequency}
                      completed={isChecked}
                      prescription={prescription_}
                    />
                  );
                },
              );
            })}
          </CardContainer>
        ) : (
          <Message>Nenhuma sequência cadastrada.</Message>
        )}
      </Section>
    ),
    [sequencies],
  );

  function FirstScreen() {
    const [isLoading, setIsLoading] = useState(false);

    const handleFinishTraining = useCallback(async () => {
      try {
        setIsLoading(true);

        await api.put(`/controlpanels/${panelOfControlId}`, { status: true });

        toast.success(`Treino finalizada com sucesso!`);

        goBack();
      } catch (err) {
        console.log(err);

        toast.error(`Erro ao finalizar treino, tente novamente!`);
      }

      setIsLoading(false);
    }, []);

    return (
      <>
        <PageHeader>
          <HeaderWrapper>
            <FirstScreenHeaderContainer>
              <PageHeaderBackButton onClick={() => goBack()}>
                <IoIosArrowBack />

                <PageHeaderBackButtonLabel>
                  protocolo de treino
                </PageHeaderBackButtonLabel>
              </PageHeaderBackButton>
              {allSequenciesIsFinished && (
                <FinishTrainingButton
                  disabled={isLoading}
                  onClick={handleFinishTraining}
                >
                  Finalizar Treino
                </FinishTrainingButton>
              )}
            </FirstScreenHeaderContainer>

            <Info>
              <InfoContent>
                <strong>Nome: </strong>
                <p>{studentData.name}</p>
              </InfoContent>

              <InfoButton type="button" onClick={() => setShowInfoModal(true)}>
                <IoMdInformationCircle color="#ffdf22" size={24} />
              </InfoButton>
            </Info>

            <Info>
              <InfoContent>
                <strong>DIVISÃO: </strong>
                <p>{division.division}</p>
              </InfoContent>

              <InfoContent>
                <strong>Segmento: </strong>
                <p>{division?.muscle_group}</p>
              </InfoContent>
            </Info>
          </HeaderWrapper>

          <NewButtonContainer>
            <NewButton onClick={() => setShowSequencesModal(true)}>
              <FiPlus color="#000" size="24" />
              <NewButtonLabel>Criar Sequência</NewButtonLabel>
            </NewButton>
          </NewButtonContainer>
        </PageHeader>

        {loading ? (
          <AlertContainer>
            <MoonLoader size={30} color="#FFDF22" />
          </AlertContainer>
        ) : (
          loadSequenciesComponent
        )}
      </>
    );
  }

  const secondScreenMemo = useMemo(
    () => (
      <SecondScreen
        prescription={prescription}
        sequencyLabel={sequencyLabel}
        studentData={studentData}
        groups={groups}
        loadSequencies={loadSequencies}
        setScreenSwitch={setScreenSwitch}
        showInfoModal={showInfoModal}
        setShowInfoModal={setShowInfoModal}
        showDeleteSequencyModal={showDeleteSequencyModal}
        setShowDeleteSequencyModal={setShowDeleteSequencyModal}
      />
    ),
    [
      prescription,
      sequencyLabel,
      studentData,
      groups,
      loadSequencies,
      setScreenSwitch,
      showInfoModal,
      setShowInfoModal,
      showDeleteSequencyModal,
      setShowDeleteSequencyModal,
    ],
  );

  return (
    <>
      <InfoModal />
      <DeleteSequencyModal />
      <SequenceModal />
      {screenSwitch ? <FirstScreen /> : secondScreenMemo}
    </>
  );
}

function SecondScreen({
  prescription,
  sequencyLabel,
  studentData,
  groups,
  loadSequencies,
  setScreenSwitch,
  showInfoModal,
  setShowInfoModal,
  showDeleteSequencyModal,
  setShowDeleteSequencyModal,
}: {
  prescription: any;
  sequencyLabel: string;
  studentData: any;
  groups: any;
  loadSequencies: () => void;
  setScreenSwitch: (v: boolean) => void;
  showInfoModal: boolean;
  setShowInfoModal: (v: boolean) => void;
  showDeleteSequencyModal: boolean;
  setShowDeleteSequencyModal: (v: boolean) => void;
}) {
  const formRef = useRef<FormHandles>(null);
  const [defaultFormData, setDefaultFormData] = useState(
    {} as InitialFormProps,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [segments, setSegments] = useState<SelectProps[]>();
  const [exercises, setExercises] = useState<SelectProps[]>([
    { value: 'Aguardando segmento...', label: 'Aguardando segmento...' },
  ]);
  const [numberOfSeries, setNumberOfSeries] = useState<SelectProps[]>([
    { value: 'Carregando...', label: 'Carregando...' },
  ]);
  const [seriesLength, setSeriesLength] = useState<number>(0);
  const [recoveryTime, setRecoveryTime] = useState('');
  const [maxChar, setMaxChar] = useState(1);
  const [isInputTimeDisabled, setIsInputTimeDisabled] =
    useState<boolean>(false);

  const [exerciseId, setExerciseId] = useState<number | null>(null);

  const handleUpdateExerciseId = (event: any) =>
    setExerciseId(event?.id || null);

  const possibleValues = useMemo(() => ['bi-set', 'tri-set'], []);
  const isBiOrTriSet = useMemo(() => {
    const description = sequencyLabel.split(' - ')[1];
    return possibleValues.includes(description);
  }, [possibleValues]);

  useEffect(() => {
    const initialFormData = {
      ...prescription,
      segment: prescription.segment && {
        label: prescription.segment || ``,
        value: prescription.segment || ``,
      },
      acessory: prescription.acessory && {
        label: prescription.acessory || ``,
        value: prescription.acessory || ``,
      },
      exercise: prescription.exercise && {
        label: prescription.exercise || ``,
        value: prescription.exercise || ``,
      },
      number_of_series: prescription.number_of_series && {
        label: String(prescription.number_of_series) || ``,
        value: String(prescription.number_of_series) || ``,
      },
    } as unknown as InitialFormProps;

    setDefaultFormData(initialFormData);

    setRecoveryTime(initialFormData.recovery_time);
  }, []);

  // Load exercises
  const loadExercises = useCallback(async (segment: string) => {
    const { data } = await api.get<ExEditorsResponse>(`ex-editors`, {
      params: {
        perpage: 9999,
        muscle_segment: segment,
      },
    });

    const mappedExercises = data[0]?.exercicies?.data?.map((exercise) => ({
      value: exercise.name,
      label: exercise.name,
      id: exercise.id,
    }));

    setExercises(mappedExercises);
  }, []);

  useEffect(() => {
    prescription?.segment && loadExercises(prescription.segment);
  }, [loadExercises]);

  // Load segments
  useEffect(() => {
    console.log('groups:', groups);

    const mappedGroups = groups.map((group_: any) => ({
      value: group_,
      label: group_,
    }));

    setSegments(mappedGroups);
  }, []);

  // Load training-stores
  const loadTrainingStores = useCallback(async () => {
    const { data } = await api.get<TrainingStoresRequestData[]>(
      'training-stores',
    );

    const numberOfSeriesData = data.filter(
      (training) => training.category === 'N° SÉRIES',
    );

    const mappedNumberOfSeries = numberOfSeriesData
      .map((serie) => ({
        value: serie.description,
        label: serie.description,
      }))
      .sort((a, b) => parseInt(a.value, 10) - parseInt(b.value, 10));

    setNumberOfSeries(mappedNumberOfSeries);
  }, []);

  useEffect(() => {
    loadTrainingStores();
  }, [loadTrainingStores]);

  const formattingRecoveryGlobalText = (value: string) => {
    const txtFaseOne = value.replace(/([0-9])([0-9])([0-9])/g, `$1$2$3/`);
    const txtFaseTwo = txtFaseOne.split('/');
    const txtFaseThree = txtFaseTwo.filter((item) => item !== '');
    const txtFaseFour = txtFaseThree.join('/');
    setRecoveryTime(txtFaseFour);
  };

  const handleChangeSeriesLenght = (e: any) => {
    const { value } = e;
    const parsedIntValue = parseInt(value || 0, 10);

    setSeriesLength(parsedIntValue);
    handleDisableRecoveryTimeInput(parsedIntValue);
    formRef.current?.setFieldValue('number_of_series', value || 0);
  };

  const handleDisableRecoveryTimeInput = (newSeriesLength: number) => {
    try {
      if (newSeriesLength < 2 && !isBiOrTriSet) {
        setIsInputTimeDisabled(true);
      } else {
        setIsInputTimeDisabled(false);
      }

      formattingRecoveryGlobalText('');
    } catch (error: any) {
      console.log('error:', error);
    }
  };

  const handleCalcMaxCharLength = () => {
    const LENGTH_PER_SERIE = 3;
    const SERIES = isBiOrTriSet ? seriesLength : seriesLength - 1;

    const BARS_LENGTH = SERIES - 1;
    const total = LENGTH_PER_SERIE * SERIES + BARS_LENGTH;
    setMaxChar(total);
  };

  useEffect(() => {
    handleCalcMaxCharLength();
  }, [seriesLength]);

  const handleSubmit = useCallback(async (data: DataFormProps) => {
    setIsLoading(true);

    const schema = Yup.object().shape({
      exercise: Yup.string().required('O campo é obrigatório'),
      charge: Yup.string().required('O campo é obrigatório'),
      number_of_series: Yup.number().required('O campo é obrigatório'),
      segment: Yup.string().required('O campo é obrigatório'),
      execution: Yup.string().required('O campo é obrigatório'),
      recovery_time: Yup.string(),
      technical_description: Yup.string().required('O campo é obrigatório'),
    });

    const formData = {
      ...data,
      recovery_time: data.recovery_time
        .replace(/_/g, '')
        .split('/')
        .filter(Boolean)
        .join('/'),
      ex_editor_id: exerciseId,
    };

    try {
      await schema.validate(formData, { abortEarly: false });

      await api.put(`/prescriptions/${prescription.id}`, formData);

      toast.success('Prescrição alterada com sucesso!');

      loadSequencies();

      setScreenSwitch(true);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        console.log(err);

        const errors = getValidationErrors(err);

        formRef.current?.setErrors(errors);

        toast.error('Preencha os campos obrigatórios e tente novamente!');
      }
    }
    setIsLoading(false);
  }, []);

  const inputStyle = {
    input: {
      height: '3.8rem',
      padding: '0 1.7rem',
    },
    textArea: {
      height: '15rem',
      padding: '2rem',
      borderRadius: '1rem',
    },
    select: {
      placeholder: () => ({
        padding: '0 1rem',
        color: '#00000080',
      }),
      singleValue: () => ({
        padding: '0 1rem',
      }),
    },
  };

  useEffect(() => {
    if (defaultFormData?.number_of_series?.value) {
      setSeriesLength(Number(defaultFormData.number_of_series.value));
    }
  }, [defaultFormData]);

  return (
    <>
      <SecondPageHeader>
        <SecondaryHeaderLine>
          <SecondaryHeaderBackButton onClick={() => setScreenSwitch(true)}>
            <IoIosArrowBack />

            <p>{sequencyLabel}</p>
          </SecondaryHeaderBackButton>

          <SecondaryHeaderContinueButton
            disabled={isLoading}
            onClick={() => {
              formRef.current?.submitForm();
            }}
          >
            Salvar
          </SecondaryHeaderContinueButton>
        </SecondaryHeaderLine>

        <SecondaryHeaderLine>
          <SecondaryHeaderInfo>
            <div>
              <strong>Nome: </strong>
              <p>{studentData.name}</p>
            </div>

            <InfoButton type="button" onClick={() => setShowInfoModal(true)}>
              <IoMdInformationCircle color="#ffdf22" size={24} />
            </InfoButton>

            <InfoButton
              type="button"
              onClick={() => setShowDeleteSequencyModal(true)}
            >
              <IoIosTrash color="red" size={24} />
            </InfoButton>
          </SecondaryHeaderInfo>
        </SecondaryHeaderLine>
      </SecondPageHeader>

      <Section>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          initialData={defaultFormData}
        >
          <RowContainer>
            <InputSet>
              <InputLabel>Segmento:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.segment}`}
                name="segment"
                options={segments}
                onChange={(e) => {
                  e && loadExercises(e.label);
                  formRef.current?.clearField('exercise');
                }}
                isSearchable={false}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>

            <InputSet>
              <InputLabel>Exercício:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.exercise}`}
                name="exercise"
                options={exercises}
                onChange={handleUpdateExerciseId}
                isSearchable={false}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>
          </RowContainer>

          <RowContainer>
            <InputSet>
              <InputLabel>Execução:</InputLabel>
              <Input
                name="execution"
                placeholder="Tipo de execução"
                fontSize="1.5rem"
                containerStyle={inputStyle.input}
              />
            </InputSet>

            <InputSet>
              <InputLabel>Número de séries:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.number_of_series}`}
                name="number_of_series"
                isSearchable={false}
                options={numberOfSeries}
                onChange={handleChangeSeriesLenght}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>
          </RowContainer>

          <RowContainer completeRow>
            <InputSet>
              <InputLabel>Descrição técnica:</InputLabel>
              <Textarea
                name="technical_description"
                containerStyle={inputStyle.textArea}
              />
            </InputSet>
          </RowContainer>

          <RowContainer>
            <InputSet>
              <InputLabel>Carga (Kg):</InputLabel>

              <Input
                name="charge"
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
                placeholder="0 KG"
              />
            </InputSet>

            <InputSet>
              <InputLabel>Tempo de recuperação:</InputLabel>
              <Input
                name="recovery_time"
                placeholder="060/030/090"
                maxLength={maxChar}
                value={recoveryTime}
                onChange={(e) => {
                  const { value } = e.target;
                  formattingRecoveryGlobalText(value);
                }}
                containerStyle={inputStyle.input}
                disabled={isInputTimeDisabled}
                fontSize="1.5rem"
              />
            </InputSet>
          </RowContainer>
        </Form>
      </Section>
    </>
  );
}
